import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { IMaskModule } from 'angular-imask'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxInputModule } from '@flowx/angular-ui-toolkit'
import { InputTemplateConfig, LabelAccessibilityProps, getMaskSettings } from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { getContentIcon, getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-input-wrapper',
  imports: [
    CommonModule,
    FlxInputModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
    IMaskModule,
  ],
  templateUrl: './flx-input-wrapper.component.html',
  styleUrl: './flx-input-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxInputWrapperComponent implements OnInit {
  #injector = inject(Injector)
  templateConfig = input.required<InputTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  maskOptions = computed(() => getMaskSettings(this.templateConfig()))
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)
  inputType = computed(() => {
    const inputType = this.flowxProps()?.inputType
    if (inputType === 'number' || inputType === 'code') {
      return 'text'
    }
    return inputType || 'text'
  })

  icons = computed(() => {
    const { leftIcon, rightIcon } = this.flowxProps()
    return {
      clear: getThemeIcon(ThemeComponent.Input, 'clearIcon'),
      helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
      leftIcon: getContentIcon(leftIcon),
      rightIcon: getContentIcon(rightIcon),
    }
  })

  // dynamic props
  label: Signal<string>
  placeholder: Signal<string>
  helper: Signal<string>
  prefix: Signal<string>
  suffix: Signal<string>
  errorMessages: Record<string, Signal<string>>
  ariaLabel: Signal<string>

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly itemId = `${this.elementUniqueId}-form-item`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  readonly CLEAR_LABEL = '@@sys_clear'

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    const config = this.templateConfig()
    runInInjectionContext(this.#injector, () => {
      const { label, placeholder = '', helper, prefix, suffix } = this.flowxProps()
      this.label = getDynamicPropSignal(label, config)
      this.placeholder = getDynamicPropSignal(placeholder, config)
      this.helper = getDynamicPropSignal(helper, config)
      this.prefix = getDynamicPropSignal(prefix, config)
      this.suffix = getDynamicPropSignal(suffix, config)
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
    })
  }
}
